import { useState, useEffect, useMemo } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { useNavigate, useParams } from "react-router-dom";
import { ReadDatabase, Entity, Query, Dimension, DimensionCollection, PaginatedEntityResponse, PaginatedQueryResponse, MatchTypeEnum } from "../../api/OasisBackendApi";
import ApplicationNavbar from "../../components/ApplicationNavbar";
import { ToastContainer } from "react-toastify";
import AnalysisSidebar from "../../components/analysis/AnalysisSidebar";
import AnalysisVisualization from "../../components/analysis/AnalysisVisualization";
import AnalysisTopBar, { AnalysisTab } from "../../components/analysis/AnalysisTopBar";
import DimensionAnalytics from "../../components/analysis/DimensionAnalytics";
import { calculateNormalComparison, calculatePCAComparison, ComparisonResult } from "../../utils/comparison";

const Analysis = () => {
    const { Database_id } = useParams();
    const navigate = useNavigate();
    const db = useOasisBackend();
    
    // State for database and basic data
    const [database, setDatabase] = useState<ReadDatabase | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // State for entities (all entities loaded)
    const [allEntities, setAllEntities] = useState<Entity[]>([]);
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    
    // State for queries (all queries loaded)
    const [allQueries, setAllQueries] = useState<Query[]>([]);
    const [queriesLoading, setQueriesLoading] = useState(false);
    
    // State for dimensions and dimension collection
    const [dimensions, setDimensions] = useState<Dimension[]>([]);
    const [dimensionCollection, setDimensionCollection] = useState<DimensionCollection | null>(null);
    const [dimensionsLoading, setDimensionsLoading] = useState(false);
    
    // State for UI mapping
    const [uiMapping, setUiMapping] = useState<Record<string, string> | null>(null);
    
    // State for selected dimensions
    const [selectedDimensions, setSelectedDimensions] = useState<{
        x: Dimension | null;
        y: Dimension | null;
    }>({ x: null, y: null });
    
    // State for visualization options
    const [showQueries, setShowQueries] = useState(false);
    const [showCoverageHull, setShowCoverageHull] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<(Entity | Query)[]>([]);
    const [searchResultEntities, setSearchResultEntities] = useState<Entity[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [distanceMetric, setDistanceMetric] = useState<"cosine" | "euclidean" | "manhattan">("cosine");
    const [searchType, setSearchType] = useState<"oasis" | "semantic">("oasis");
    const [topK, setTopK] = useState<number>(10);
    const [useOasisWeights, setUseOasisWeights] = useState(false);
    const [queryMatches, setQueryMatches] = useState<Record<string, MatchTypeEnum>>({});
    
    // State for weight-adjusted visualization mode
    const [weightAdjustedMode, setWeightAdjustedMode] = useState(false);
    const [queryWeights, setQueryWeights] = useState<Record<string, number>>({});
    const [currentSearchQueryId, setCurrentSearchQueryId] = useState<string | null>(null);
    
    // State for overlap viewer
    const [selectedOverlapGroup, setSelectedOverlapGroup] = useState<any[] | null>(null);
    const [overlapIndex, setOverlapIndex] = useState(0);
    
    // State for PCA
    const [usePCAMode, setUsePCAMode] = useState(false);
    const [pcaSelectedDimensions, setPcaSelectedDimensions] = useState<Dimension[]>([]);
    const [pcaResult, setPcaResult] = useState<any>(null);
    const [pcaBasisSource, setPcaBasisSource] = useState<"both" | "entities" | "queries">("both");
    
    // State for normalized vs raw embedding toggle
    const [useNormalizedEmbeddings, setUseNormalizedEmbeddings] = useState(false);
    
    // State for vector field visualization
    const [showVectorField, setShowVectorField] = useState(false);
    const [vectorFieldMode, setVectorFieldMode] = useState<"individual" | "average">("individual");
    const [vectorFieldLength, setVectorFieldLength] = useState<"field" | "full">("field");
    const [vectorFieldShimmer, setVectorFieldShimmer] = useState(false);
    const [queryMatchesLoaded, setQueryMatchesLoaded] = useState(false);
    
    // Make modes mutually exclusive: when PCA mode is enabled, clear selected dimensions
    useEffect(() => {
        if (usePCAMode) {
            setSelectedDimensions({ x: null, y: null });
        }
    }, [usePCAMode]);
    
    // Make modes mutually exclusive: when dimensions are selected, disable PCA mode
    useEffect(() => {
        if (selectedDimensions.x || selectedDimensions.y) {
            if (usePCAMode) {
                setUsePCAMode(false);
            }
        }
    }, [selectedDimensions.x, selectedDimensions.y]);
    
    // State for tabs
    const [activeTab, setActiveTab] = useState<AnalysisTab>('dimensionality');
    
    // State for comparison mode
    const [isComparisonMode, setIsComparisonMode] = useState(false);
    const [selectedPoints, setSelectedPoints] = useState<[any | null, any | null]>([null, null]);
    const [hoveredDimensionId, setHoveredDimensionId] = useState<string | null>(null);
    
    // Calculate comparison result
    const comparisonResult = useMemo<ComparisonResult | null>(() => {
        if (!selectedPoints[0] || !selectedPoints[1]) return null;
        
        const point1 = selectedPoints[0];
        const point2 = selectedPoints[1];
        
        // Ensure points have coordinates
        if (point1.x === undefined || point1.y === undefined || 
            point2.x === undefined || point2.y === undefined) {
            return null;
        }
        
        if (usePCAMode && pcaResult) {
            return calculatePCAComparison(
                point1,
                point2,
                pcaResult,
                pcaSelectedDimensions,
                useNormalizedEmbeddings
            );
        } else {
            const liveDimensions = dimensionCollection?.dimensions || [];
            const availableDimensions = liveDimensions.length > 0 ? liveDimensions : dimensions;
            return calculateNormalComparison(
                point1,
                point2,
                availableDimensions,
                selectedDimensions,
                useNormalizedEmbeddings
            );
        }
    }, [selectedPoints, usePCAMode, pcaResult, pcaSelectedDimensions, dimensions, dimensionCollection, selectedDimensions, useNormalizedEmbeddings]);

    // Fetch database information
    useEffect(() => {
        if (!Database_id) {
            navigate("/playground");
            return;
        }
        
        const fetchDatabase = async () => {
            try {
                setIsLoading(true);
                const response = await db.endpoints.api.apiDatabasesRetrieve(Database_id);
                setDatabase(response.data);
            } catch (error) {
                console.error("Error fetching database:", error);
                setError("Failed to load database");
                navigate("/playground");
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchDatabase();
    }, [Database_id, navigate, db.endpoints]);

    // Fetch all entities with pagination
    const fetchAllEntities = async () => {
        if (!database?.primary_entity_collection_id) return;
        
        try {
            setEntitiesLoading(true);
            const allEntitiesData: Entity[] = [];
            let page = 1;
            let hasMore = true;
            
            while (hasMore) {
                const response = await db.endpoints.api.apiDatabaseEntityCollectionAllEntitiesRetrieve(
                    database.id,
                    { page, page_size: 100 }
                );
                
                const paginatedData = response.data as PaginatedEntityResponse;
                allEntitiesData.push(...paginatedData.results);
                
                hasMore = !!paginatedData.next;
                page++;
            }
            
            setAllEntities(allEntitiesData);
            
            // Fetch UI mapping from entity collection
            try {
                const entityCollectionResponse = await db.endpoints.api.apiDatabaseEntityCollectionRetrieve(
                    database.id
                );
                setUiMapping(entityCollectionResponse.data.ui_mapping);
                console.log('Loaded UI mapping:', entityCollectionResponse.data.ui_mapping);
            } catch (error) {
                console.error("Error fetching UI mapping:", error);
            }
        } catch (error) {
            console.error("Error fetching entities:", error);
            setError("Failed to load entities");
        } finally {
            setEntitiesLoading(false);
        }
    };

    // Fetch all queries with pagination
    const fetchAllQueries = async (includeMatches: boolean = false) => {
        if (!database?.primary_entity_collection_id) return;
        
        try {
            setQueriesLoading(true);
            const allQueriesData: Query[] = [];
            let page = 1;
            let hasMore = true;
            
            while (hasMore) {
                const response = await db.endpoints.api.apiEntityCollectionQueriesRetrieve(
                    database.primary_entity_collection_id,
                    { page, page_size: 25, include_query_matches: includeMatches }
                );
                
                const paginatedData = response.data as PaginatedQueryResponse;
                allQueriesData.push(...paginatedData.results);
                
                hasMore = !!paginatedData.next;
                page++;
            }
            
            setAllQueries(allQueriesData);
        } catch (error) {
            console.error("Error fetching queries:", error);
            setError("Failed to load queries");
        } finally {
            setQueriesLoading(false);
        }
    };

    // Fetch dimensions and live dimension collection
    const fetchDimensions = async () => {
        if (!database?.primary_entity_collection_id) return;
        
        try {
            setDimensionsLoading(true);
            
            // Fetch all dimensions
            const dimensionsResponse = await db.endpoints.api.apiEntityCollectionDimensionsList(
                database.primary_entity_collection_id
            );
            setDimensions(dimensionsResponse.data);
            
            // Fetch live dimension collection if it exists
            if (database.live_dimension_collection_id) {
                const dimensionCollectionResponse = await db.endpoints.api.apiDimensionCollectionRetrieve(
                    database.live_dimension_collection_id
                );
                setDimensionCollection(dimensionCollectionResponse.data);
            }
        } catch (error) {
            console.error("Error fetching dimensions:", error);
            setError("Failed to load dimensions");
        } finally {
            setDimensionsLoading(false);
        }
    };

    // Load all data when database is available
    useEffect(() => {
        if (database) {
            fetchAllEntities();
            fetchAllQueries();
            fetchDimensions();
        }
    }, [database]);

    // Re-fetch queries with matches when vector field is enabled
    useEffect(() => {
        if (showVectorField && !queryMatchesLoaded && database) {
            fetchAllQueries(true).then(() => setQueryMatchesLoaded(true));
        }
    }, [showVectorField, queryMatchesLoaded, database]);

    // Debug logging
    useEffect(() => {
        if (allEntities.length > 0) {
            console.log('Loaded entities:', allEntities.length);
            console.log('Sample entity structure:', allEntities[0]);
            if (allEntities[0].embedding?.metadata?.entity_dimension_index) {
                console.log('Available dimensions in first entity:', Object.keys(allEntities[0].embedding.metadata.entity_dimension_index));
            }
        }
    }, [allEntities]);

    useEffect(() => {
        // Use the same logic as sidebar: liveDimensions if available, otherwise dimensions
        // Only initialize if dimensions are loaded and we haven't initialized yet
        if (dimensions.length === 0) return; // Wait for dimensions to load
        
        const liveDimensions = dimensionCollection?.dimensions || [];
        const availableDimensions = liveDimensions.length > 0 ? liveDimensions : dimensions;
        
        // Only initialize if we haven't set PCA dimensions yet, or if the available dimensions changed
        if (pcaSelectedDimensions.length === 0 && availableDimensions.length > 0) {
            console.log('Initializing PCA dimensions:', availableDimensions.length, 'dimensions');
            // Initialize PCA dimensions to all available dimensions by default
            setPcaSelectedDimensions([...availableDimensions]);
        } else if (pcaSelectedDimensions.length > 0 && availableDimensions.length > 0) {
            // If dimensions changed (e.g., dimensionCollection loaded), update if needed
            const currentIds = new Set(pcaSelectedDimensions.map(d => d.id));
            const availableIds = new Set(availableDimensions.map(d => d.id));
            
            // If the available dimensions are different, update the selection
            if (currentIds.size !== availableIds.size || 
                !Array.from(currentIds).every(id => availableIds.has(id))) {
                console.log('Updating PCA dimensions to match available dimensions');
                setPcaSelectedDimensions([...availableDimensions]);
            }
        }
    }, [dimensions, dimensionCollection]);

    useEffect(() => {
        if (allQueries.length > 0) {
            console.log('Loaded queries:', allQueries.length);
            console.log('Sample query structure:', allQueries[0]);
            if (allQueries[0].oasis_embedding) {
                console.log('Available dimensions in first query:', Object.keys(allQueries[0].oasis_embedding));
            }
        }
    }, [allQueries]);

    // Handle search
    const handleSearch = async (query: string, metric: "cosine" | "euclidean" | "manhattan" = distanceMetric, type: "oasis" | "semantic" = searchType, k: number = topK, useWeights: boolean = useOasisWeights) => {
        if (!query.trim()) {
            // Clear search results
            setSearchResults([]);
            setSearchResultEntities([]);
            setQueryMatches({});
            setQueryWeights({});
            setCurrentSearchQueryId(null);
            setWeightAdjustedMode(false);
            return;
        }
        
        if (!database) return;
        
        try {
            setSearchLoading(true);
            
            // First, perform the search to get results
            const searchResponse = await db.endpoints.api.apiDatabaseSearchCreate(database.id, {
                query: query.trim(),
                type: type,
                rerank: false,
                distance_metric: type === "oasis" ? metric : undefined,
                limit: k,
                use_oasis_weights: type === "oasis" ? useWeights : undefined
            });
            
            console.log('Search response:', searchResponse.data);
            
            // Store the search result entities for coverage analysis
            if (type === "oasis" && searchResponse.data?.oasis?.results) {
                setSearchResultEntities(searchResponse.data.oasis.results);
                console.log('Search result entities stored:', searchResponse.data.oasis.results.length);
            } else if (type === "semantic" && searchResponse.data?.semantic) {
                setSearchResultEntities(searchResponse.data.semantic);
                console.log('Semantic search result entities stored:', searchResponse.data.semantic.length);
            } else {
                setSearchResultEntities([]);
                console.log('No search result entities found');
            }
            
            // Only fetch query embedding for Oasis search (to show search location)
            if (type === "oasis") {
                // Then, fetch the actual query with proper embedding using the query-by-text endpoint
                const queryResponse = await db.endpoints.api.apiEntityCollectionQueryByTextRetrieve(
                    database.primary_entity_collection_id,
                    { text: query.trim() }
                );
                
                console.log('Query by text response:', queryResponse.data);
                
                if (queryResponse.data) {
                    // Use the actual query object with proper embedding
                    setSearchResults([queryResponse.data]);
                    
                    // Store the query ID for weight fetching
                    setCurrentSearchQueryId(queryResponse.data.id);
                    
                    // Extract query matches and store them for entity coloring
                    if (queryResponse.data.query_matches && queryResponse.data.query_matches.length > 0) {
                        const matchesMap: Record<string, MatchTypeEnum> = {};
                        queryResponse.data.query_matches.forEach((match) => {
                            matchesMap[match.entity_id] = match.match_type;
                        });
                        setQueryMatches(matchesMap);
                        console.log('Query matches loaded:', Object.keys(matchesMap).length);
                    } else {
                        setQueryMatches({});
                        console.log('No query matches found');
                    }
                    
                    // Fetch query weights if using weighted search
                    if (useWeights && queryResponse.data.id) {
                        try {
                            const weightsResponse = await db.endpoints.instance.get(
                                `/api/queries/${queryResponse.data.id}/weights/`
                            );
                            if (weightsResponse.data?.weights) {
                                setQueryWeights(weightsResponse.data.weights);
                                console.log('Query weights loaded:', Object.keys(weightsResponse.data.weights).length);
                            }
                        } catch (weightError) {
                            console.log('Could not fetch query weights:', weightError);
                            setQueryWeights({});
                        }
                    } else {
                        setQueryWeights({});
                    }
                } else {
                    console.log('No query found by text');
                    setSearchResults([]);
                    setQueryMatches({});
                    setQueryWeights({});
                    setCurrentSearchQueryId(null);
                }
            } else {
                // For semantic search, don't show the search location or matches
                setSearchResults([]);
                setQueryMatches({});
                setQueryWeights({});
                setCurrentSearchQueryId(null);
                setWeightAdjustedMode(false);
            }
        } catch (error) {
            console.error("Error performing search:", error);
            setError("Search failed");
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle overlap group selection
    const handleOverlapGroupSelect = (group: any[] | null, index: number) => {
        setSelectedOverlapGroup(group);
        setOverlapIndex(index);
    };

    const handleOverlapIndexChange = (index: number) => {
        setOverlapIndex(index);
    };

    const handleCloseOverlapViewer = () => {
        setSelectedOverlapGroup(null);
        setOverlapIndex(0);
    };

    // Loading screen
    if (isLoading) {
        return (
            <div className="flex flex-col min-h-screen pt-16 bg-gray-900 text-white">
                <ApplicationNavbar />
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                        <p className="text-lg">Loading analysis...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error screen
    if (error || !database) {
        return (
            <div className="flex flex-col min-h-screen pt-16 bg-gray-900 text-white">
                <ApplicationNavbar />
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <p className="text-lg text-red-400">{error || "Database not found"}</p>
                        <button 
                            onClick={() => navigate("/playground")}
                            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            Back to Playground
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen pt-16 bg-gray-900 text-white">
            <ApplicationNavbar />
            <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                theme="dark"
            />
            
            <div className="flex flex-1 overflow-hidden min-h-0">
                {/* Sidebar */}
                <AnalysisSidebar
                    dimensions={dimensions}
                    dimensionCollection={dimensionCollection}
                    selectedDimensions={selectedDimensions}
                    onDimensionsChange={setSelectedDimensions}
                    showQueries={showQueries}
                    onShowQueriesChange={setShowQueries}
                    showCoverageHull={showCoverageHull}
                    onShowCoverageHullChange={setShowCoverageHull}
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                    onSearch={handleSearch}
                    searchLoading={searchLoading}
                    distanceMetric={distanceMetric}
                    onDistanceMetricChange={setDistanceMetric}
                    searchType={searchType}
                    onSearchTypeChange={setSearchType}
                    topK={topK}
                    onTopKChange={setTopK}
                    useOasisWeights={useOasisWeights}
                    onUseOasisWeightsChange={setUseOasisWeights}
                    searchResultEntities={searchResultEntities}
                    entitiesLoading={entitiesLoading}
                    queriesLoading={queriesLoading}
                    dimensionsLoading={dimensionsLoading}
                    selectedOverlapGroup={selectedOverlapGroup}
                    overlapIndex={overlapIndex}
                    onOverlapIndexChange={handleOverlapIndexChange}
                    onCloseOverlapViewer={handleCloseOverlapViewer}
                    uiMapping={uiMapping}
                    usePCAMode={usePCAMode}
                    onUsePCAModeChange={setUsePCAMode}
                    pcaSelectedDimensions={pcaSelectedDimensions}
                    onPcaSelectedDimensionsChange={setPcaSelectedDimensions}
                    pcaResult={pcaResult}
                    activeTab={activeTab}
                    isComparisonMode={isComparisonMode}
                    onComparisonModeChange={setIsComparisonMode}
                    selectedPoints={selectedPoints}
                    onSelectedPointsChange={setSelectedPoints}
                    hoveredDimensionId={hoveredDimensionId}
                    onHoveredDimensionChange={setHoveredDimensionId}
                    comparisonResult={comparisonResult}
                    weightAdjustedMode={weightAdjustedMode}
                    onWeightAdjustedModeChange={setWeightAdjustedMode}
                    queryWeights={queryWeights}
                    useNormalizedEmbeddings={useNormalizedEmbeddings}
                    onUseNormalizedEmbeddingsChange={setUseNormalizedEmbeddings}
                    pcaBasisSource={pcaBasisSource}
                    onPcaBasisSourceChange={setPcaBasisSource}
                    showVectorField={showVectorField}
                    onShowVectorFieldChange={setShowVectorField}
                    vectorFieldMode={vectorFieldMode}
                    onVectorFieldModeChange={setVectorFieldMode}
                    vectorFieldLength={vectorFieldLength}
                    onVectorFieldLengthChange={setVectorFieldLength}
                    vectorFieldShimmer={vectorFieldShimmer}
                    onVectorFieldShimmerChange={setVectorFieldShimmer}
                />
                
                {/* Main visualization area */}
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="p-3 border-b border-gray-700 flex-shrink-0">
                        <h1 className="text-xl font-bold text-white">Analysis: {database.name}</h1>
                    </div>
                    
                    {/* Top Bar with Tabs - Floating (notch design) */}
                    <AnalysisTopBar activeTab={activeTab} onTabChange={setActiveTab} />
                    
                    {/* Spacer for floating bar */}
                    <div className="h-12 flex-shrink-0"></div>
                    
                    <div className="flex-1 min-h-0">
                        {activeTab === 'analytics' ? (
                            database?.primary_entity_collection_id ? (
                                <DimensionAnalytics entityCollectionId={database.primary_entity_collection_id} />
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-gray-400">No entity collection available</div>
                                </div>
                            )
                        ) : (
                            <AnalysisVisualization
                                entities={allEntities}
                                queries={(showQueries || showVectorField) ? allQueries : []}
                                searchResults={searchResults}
                                searchResultEntities={searchResultEntities}
                                selectedDimensions={selectedDimensions}
                                showCoverageHull={showCoverageHull}
                                isLoading={entitiesLoading || queriesLoading || dimensionsLoading}
                                onOverlapGroupSelect={handleOverlapGroupSelect}
                                uiMapping={uiMapping}
                                usePCAMode={usePCAMode}
                                pcaSelectedDimensions={pcaSelectedDimensions}
                                onPcaResultChange={setPcaResult}
                                isComparisonMode={isComparisonMode}
                                selectedPoints={selectedPoints}
                                onSelectedPointsChange={setSelectedPoints}
                                hoveredDimensionId={hoveredDimensionId}
                                comparisonResult={comparisonResult}
                                queryMatches={queryMatches}
                                showVectorField={showVectorField}
                                vectorFieldMode={vectorFieldMode}
                                vectorFieldLength={vectorFieldLength}
                                vectorFieldShimmer={vectorFieldShimmer}
                                weightAdjustedMode={weightAdjustedMode}
                                queryWeights={queryWeights}
                                distanceMetric={distanceMetric}
                                useNormalizedEmbeddings={useNormalizedEmbeddings}
                                pcaBasisSource={pcaBasisSource}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analysis;
